/**
 * Removes stamp free-gift lines when their linked Best Deal stamp line is removed.
 * Main + gift are linked via `_stamp_gift_bundle` line properties set at add-to-cart.
 */

const BUNDLE_PROP = '_stamp_gift_bundle';
const ROLE_PROP = '_stamp_gift_role';
const SOURCE = 'stamp-gift-manager';

class StampGiftManager {
  #processing = false;
  #pending = false;
  #debounceTimer = null;
  /** @type {{ giftVariantId?: string|number, triggerVariantIds?: Array<string|number> }|null} */
  #config = null;

  constructor() {
    this.#loadConfig();

    document.addEventListener('cart:update', (event) => {
      if (event.detail?.data?.source === SOURCE) return;
      // Removal is handled inline via syncMainLineQuantity (single cart/update.js).
      if (event.detail?.data?.source === 'cart-items-component') return;
      this.#scheduleReconcile(120);
    });

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.#scheduleReconcile(0));
    } else {
      this.#scheduleReconcile(0);
    }
  }

  get isBusy() {
    return this.#processing;
  }

  willSyncStampGiftRemoval() {
    return true;
  }

  willSyncStampGiftQuantity() {
    return true;
  }

  /** @param {object} item */
  #isStampMain(item) {
    const props = item.properties || {};
    return props[ROLE_PROP] === 'main' || props._includes_stamp_gift === 'true';
  }

  /**
   * @param {object} mainItem
   * @param {object[]} items
   * @returns {object[]}
   */
  #linkedBonuses(mainItem, items) {
    const props = mainItem.properties || {};
    const bundleId = props[BUNDLE_PROP];

    if (bundleId) {
      return items.filter((item) => {
        if (item.key === mainItem.key) return false;
        const itemProps = item.properties || {};
        return itemProps[ROLE_PROP] === 'bonus' && itemProps[BUNDLE_PROP] === bundleId;
      });
    }

    return items.filter((item) => {
      if (item.key === mainItem.key || !this.#isStampGift(item)) return false;
      const parentVariantId = item.properties?._stamp_gift_for;
      return parentVariantId && String(parentVariantId) === String(mainItem.variant_id);
    });
  }

  /**
   * Remove (or update) a stamp main line and its linked gift in one cart/update.js request.
   * @param {number} lineIndex - 1-based cart line index
   * @param {number} newQty
   * @param {string[]} sectionIds
   * @returns {Promise<object|null>}
   */
  async syncMainLineQuantity(lineIndex, newQty, sectionIds, lineKey) {
    this.#processing = true;

    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!response.ok) return null;

      const cart = await response.json();
      const items = cart.items || [];
      const mainItem = lineKey
        ? items.find((item) => item.key === lineKey)
        : items[lineIndex - 1];
      if (!mainItem || !this.#isStampMain(mainItem)) return null;

      const mainQty = Math.max(parseInt(String(newQty), 10) || 0, 0);
      /** @type {Record<string, number>} */
      const updates = { [mainItem.key]: mainQty };

      const bonuses = this.#linkedBonuses(mainItem, items);

      bonuses.forEach((bonus) => {
        updates[bonus.key] = mainQty > 0 ? mainQty : 0;
      });

      if (bonuses.length === 0 && mainQty > 0) {
        const giftVariantId = this.#config?.giftVariantId;
        if (giftVariantId) {
          const bundleId =
            mainItem.properties?.[BUNDLE_PROP] ||
            `sg-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

          const cartData = await this.#postCartUpdate(updates, sectionIds);
          if (!cartData) return null;

          /** @type {Record<string, unknown>} */
          const addBody = {
            items: [
              {
                id: parseInt(String(giftVariantId), 10),
                quantity: mainQty,
                properties: {
                  _free_gift: 'true',
                  _stamp_gift_bundle: bundleId,
                  _stamp_gift_role: 'bonus',
                  _stamp_gift_for: String(mainItem.variant_id),
                },
              },
            ],
          };

          if (sectionIds?.length) {
            addBody.sections = sectionIds.join(',');
            addBody.sections_url = window.location.pathname;
          }

          const addResponse = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(addBody),
          });

          if (!addResponse.ok) {
            console.warn('[StampGiftManager] add.js failed while syncing gift quantity');
            return cartData;
          }

          return addResponse.json();
        }
      }

      return await this.#postCartUpdate(updates, sectionIds);
    } catch (error) {
      console.warn('[StampGiftManager] syncMainLineQuantity failed', error);
      return null;
    } finally {
      this.#processing = false;
    }
  }

  #loadConfig() {
    const el = document.getElementById('StampGiftConfig');
    if (!el) return;
    try {
      this.#config = JSON.parse(el.textContent);
    } catch (error) {
      console.warn('[StampGiftManager] Could not parse config', error);
    }
  }

  /** @param {number} [delay] */
  #scheduleReconcile(delay = 120) {
    clearTimeout(this.#debounceTimer);
    this.#debounceTimer = setTimeout(() => this.#reconcile(), delay);
  }

  #getSectionIds() {
    return Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);
  }

  /** @param {object} item */
  #isStampGift(item) {
    const props = item.properties || {};
    if (props[ROLE_PROP] === 'bonus') return true;
    if (props._free_gift !== 'true') return false;

    const giftVariantId = this.#config?.giftVariantId;
    if (giftVariantId) {
      return String(item.variant_id) === String(giftVariantId);
    }

    return Boolean(props[BUNDLE_PROP] || props._stamp_gift_for);
  }

  /** @param {object[]} items */
  #countTriggerVariants(items) {
    const triggerIds = (this.#config?.triggerVariantIds || []).map(String).filter(Boolean);
    if (!triggerIds.length) return null;

    return items.reduce((count, item) => {
      if (triggerIds.includes(String(item.variant_id))) {
        return count + (parseInt(item.quantity, 10) || 1);
      }
      return count;
    }, 0);
  }

  /**
   * @param {Record<string, number>} updates
   * @param {string[]} [sectionIds]
   */
  async #postCartUpdate(updates, sectionIds) {
    /** @type {Record<string, unknown>} */
    const body = { updates };
    if (sectionIds?.length) {
      body.sections = sectionIds.join(',');
      body.sections_url = window.location.pathname;
    }

    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      console.warn('[StampGiftManager] update.js failed');
      return null;
    }

    return response.json();
  }

  /** @param {object[]} items */
  async #removeOrphanGifts(items) {
    /** @type {Record<string, number>} */
    const updates = {};

    const mainBundleIds = new Set(
      items
        .filter((item) => {
          const props = item.properties || {};
          return props[ROLE_PROP] === 'main' && props[BUNDLE_PROP];
        })
        .map((item) => item.properties[BUNDLE_PROP])
    );

    const triggerVariantCount = this.#countTriggerVariants(items);

    items.forEach((item) => {
      if (!this.#isStampGift(item)) return;

      const props = item.properties || {};
      const bundleId = props[BUNDLE_PROP];

      if (bundleId) {
        if (!mainBundleIds.has(bundleId)) {
          updates[item.key] = 0;
        }
        return;
      }

      const parentVariantId = props._stamp_gift_for;
      if (parentVariantId) {
        const hasParent = items.some(
          (candidate) => String(candidate.variant_id) === String(parentVariantId)
        );
        if (!hasParent) {
          updates[item.key] = 0;
        }
        return;
      }

      if (props._free_gift === 'true' && triggerVariantCount === 0) {
        updates[item.key] = 0;
      }
    });

    items.forEach((item) => {
      if (!this.#isStampMain(item)) return;

      const mainQty = parseInt(item.quantity, 10) || 1;
      this.#linkedBonuses(item, items).forEach((bonus) => {
        if (updates[bonus.key] === 0) return;

        const bonusQty = parseInt(bonus.quantity, 10) || 0;
        if (bonusQty !== mainQty) {
          updates[bonus.key] = mainQty;
        }
      });
    });

    const keys = Object.keys(updates);
    if (!keys.length) return null;

    const sectionIds = this.#getSectionIds();
    const cartData = await this.#postCartUpdate(updates, sectionIds);

    if (cartData) {
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cartData,
            sourceId: SOURCE,
            data: {
              source: SOURCE,
              itemCount: cartData.item_count,
              sections: cartData.sections || {},
            },
          },
        })
      );
    }

    return cartData;
  }

  async #reconcile() {
    if (this.#processing) {
      this.#pending = true;
      return;
    }

    this.#processing = true;

    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!response.ok) return;

      const cart = await response.json();
      await this.#removeOrphanGifts(cart.items || []);
    } catch (error) {
      console.warn('[StampGiftManager] reconcile failed', error);
    } finally {
      this.#processing = false;
      if (this.#pending) {
        this.#pending = false;
        this.#scheduleReconcile(0);
      }
    }
  }
}

if (!window.__stampGiftManager) {
  window.__stampGiftManager = new StampGiftManager();
}
